/* ROUTER */
import { Link } from 'react-router-dom';

/* PLUGINS */
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, Mail, Phone, User } from 'lucide-react';
import { useForm } from 'react-hook-form';

/* COMPONENTS */
import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { FormInput, PasswordInput } from '@/components/forms';

/* HOOKS */
import { useRegister } from '../hooks';
import { useUserRoles } from '../hooks/useUserRoles';

/* SCHEMA */
import { registerSchema, type RegisterFormData } from '../schemas';

/* COMPONENTS */
import { AuthLayout } from './AuthLayout';

/**
 * DOCU: Renders the registration form with user details, role selection, and password fields using shadcn/ui components. <br>
 * Triggered: When user navigates to the registration page (/register). <br>
 * Last Updated Date: December 1, 2025 <br>
 * @author Kirt
 */
export const RegistrationForm = () => {
    const { mutate: register, isPending } = useRegister();

    // Fetch roles from API only - no fallback
    const { data: rawRoles = [], isLoading: is_loading_roles, error: roles_error } = useUserRoles();

    /* Hide the Admin (system administrator) option from public account creation — only Project Manager
       and Supervisor are self-serve. Filter is by role name (id ordering can shift across environments). */
    const roles = rawRoles.filter((role) => role.name.toLowerCase() !== 'admin');

    const form = useForm<RegisterFormData & { confirmPassword: string }>({
        resolver: zodResolver(
            registerSchema.extend({
                confirmPassword: registerSchema.shape.password,
            }).refine((data) => data.password === data.confirmPassword, {
                message: 'Passwords do not match',
                path: ['confirmPassword'],
            })
        ),
        /* Leave role_type_id undefined so the "-- Select Role --" placeholder shows until the user picks
           a role. Zod's positive() validation then flags an unselected role on submit. */
        defaultValues: {
            role_type_id: undefined as unknown as number,
        },
    });

    /**
     * DOCU: Handles form submission, removes confirmPassword field, and triggers registration mutation. <br>
     * Triggered: When user submits the registration form. <br>
     * Last Updated Date: December 1, 2025 <br>
     * @param data - Validated form data containing user details, password, and confirmPassword.
     * @author Kirt
     */
    const onSubmit = (data: RegisterFormData & { confirmPassword: string }) => {
        const { confirmPassword, ...registerData } = data;
        register(registerData);
    };

    return (
        <AuthLayout title="Create Account" showBackButton>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-4 items-start">
                        <FormField
                            control={form.control}
                            name="first_name"
                            render={({ field }) => (
                                <FormInput
                                    field={field}
                                    label="First Name"
                                    placeholder="John"
                                    disabled={isPending}
                                    required
                                    icon={User}
                                />
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="last_name"
                            render={({ field }) => (
                                <FormInput
                                    field={field}
                                    label="Last Name"
                                    placeholder="Doe"
                                    disabled={isPending}
                                    required
                                    icon={User}
                                />
                            )}
                        />
                    </div>

                    {/* Email Field */}
                    <FormField
                        control={form.control}
                        name="email_address"
                        render={({ field }) => (
                            <FormInput
                                field={field}
                                label="Email Address"
                                type="email"
                                placeholder="your.email@company.com"
                                disabled={isPending}
                                required
                                icon={Mail}
                            />
                        )}
                    />

                    {/* Company Field */}
                    <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                            <FormInput
                                field={field}
                                label="Company Name"
                                placeholder="Your Company Name"
                                disabled={isPending}
                                required
                                icon={Building2}
                            />
                        )}
                    />

                    {/* Phone Field (Optional) */}
                    <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                            <FormInput
                                field={field}
                                label="Phone Number"
                                type="tel"
                                placeholder="+1 (555) 123-4567"
                                disabled={isPending}
                                icon={Phone}
                            />
                        )}
                    />

                    {/* Role Selection */}
                    <FormField
                        control={form.control}
                        name="role_type_id"
                        render={({ field }) => (
                            <FormItem className="space-y-0">
                                <FormLabel className="block text-foreground mb-0 font-normal text-base">
                                    Role <span className="text-destructive">*</span>
                                </FormLabel>
                                {roles_error && (
                                    <div className="mb-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg" role="alert">
                                        <p className="text-sm text-destructive">
                                            Failed to load roles. Please refresh the page or try again later.
                                        </p>
                                    </div>
                                )}
                                <Select
                                    value={field.value?.toString()}
                                    onValueChange={(value) => field.onChange(Number(value))}
                                    disabled={isPending || is_loading_roles}
                                >
                                    <FormControl>
                                        <SelectTrigger className="w-full max-w-full px-4 py-3 border-2 border-border rounded-lg focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand transition-colors bg-background overflow-hidden [&>span:first-child]:truncate [&>span:first-child]:block [&>span:first-child]:max-w-[calc(100%-20px)]">
                                            <SelectValue
                                                className="truncate"
                                                placeholder={
                                                    is_loading_roles
                                                        ? 'Loading roles...'
                                                        : roles_error
                                                        ? 'Error loading roles'
                                                        : '-- Select Role --'
                                                }
                                            />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent className="max-h-[300px] w-[var(--radix-select-trigger-width)] bg-popover border border-border rounded-lg shadow-lg">
                                        {roles.map((role) => (
                                            <SelectItem
                                                key={role.id}
                                                value={role.id.toString()}
                                                className="p-2 cursor-pointer hover:bg-accent focus:bg-accent"
                                                textValue={role.name.charAt(0).toUpperCase() + role.name.slice(1).replace(/_/g, ' ')}
                                            >
                                                <span className="truncate">
                                                    {role.name.charAt(0).toUpperCase() + role.name.slice(1).replace(/_/g, ' ')}
                                                    {role.description ? ` - ${role.description}` : ''}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage className="mt-1 text-sm text-destructive" role="alert" />
                            </FormItem>
                        )}
                    />

                    {/* Password Field */}
                    <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                            <PasswordInput
                                field={field}
                                label="Password"
                                placeholder="Min. 6 characters (A-Z, a-z, 0-9)"
                                disabled={isPending}
                                required
                            />
                        )}
                    />

                    {/* Confirm Password Field */}
                    <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                            <PasswordInput
                                field={field}
                                label="Confirm Password"
                                placeholder="Re-enter your password"
                                disabled={isPending}
                                required
                            />
                        )}
                    />

                    {/* Create Account Button */}
                    <Button
                        type="submit"
                        disabled={isPending}
                        className="w-full bg-brand text-brand-foreground py-3 rounded-lg hover:bg-brand-hover transition-all shadow-lg hover:shadow-xl active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending ? 'Creating Account...' : 'Create Account'}
                    </Button>
                </form>
            </Form>

            {/* Already have an account */}
            <div className="mt-6 text-center">
                <p className="text-muted-foreground">
                    Already have an account?{' '}
                    <Link
                        to="/login"
                        className="text-brand hover:text-brand-hover font-semibold transition-colors"
                    >
                        Sign In
                    </Link>
                </p>
            </div>
        </AuthLayout>
    );
};
